import { ButtonColor } from "@/common/enum/button-color";
import { TextInputColor } from "@/common/enum/text-input-color";
import { validatePassword } from "@/common/validator/validate-password";
import { Button, Modal, TextInput } from "flowbite-react";
import React, { useEffect, useState } from "react";
import { useSetRecoilState } from "recoil";
import { toastState } from "../../atoms/system/toast-state";
import { userService } from "../../services/user-service";
import { UserDto } from "../../types/dto/user-dto";
import { CustomHelperText } from "../CustomHelperText";
import { PasswordEyeIcon } from "../icon/PasswordEyeIcon";
import { TextInputLabel } from "../label/TextInputLabel";
import { H4 } from "../typography/H4";

interface Props {
  user: UserDto;
  closeModal: () => void;
}

const initInputs = {
  oldPassword: "",
  newPassword: "",
  newPasswordConfirm: "",
};
export const AdminPasswordModal: React.FC<Props> = ({ user, closeModal }) => {
  const [inputs, setInputs] = useState(initInputs);
  const setToast = useSetRecoilState(toastState);
  const [aggregateErrors, setAggregateErrors] = useState({
    oldPassword: "",
    newPassword: "",
    newPasswordConfirm: "",
  });

  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showNewPasswordConfirm, setShowNewPasswordConfirm] = useState(false);

  const toggleShowOldPassword = () => {
    setShowOldPassword(!showOldPassword);
  };

  const toggleShowNewPassword = () => {
    setShowNewPassword(!showNewPassword);
  };

  const toggleShowNewPasswordConfirm = () => {
    setShowNewPasswordConfirm(!showNewPasswordConfirm);
  };

  const handleTextInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setInputs({ ...inputs, [name]: value });
    if (name === "newPasswordConfirm") {
      if (value !== inputs.newPassword) {
        setAggregateErrors({
          ...aggregateErrors,
          newPasswordConfirm: "현재 비밀번호와 다릅니다.",
        });
      } else {
        setAggregateErrors({
          ...aggregateErrors,
          newPasswordConfirm: "",
        });
      }
    }
  };

  const handleSubmit = async () => {
    if (!validatePassword(inputs.newPassword)) {
      setAggregateErrors({
        ...aggregateErrors,
        newPassword: "특수문자, 영어, 숫자 조합으로 8~15자를 입력해주세요.",
      });
      return;
    }
    const payload = {
      oldPassword: inputs.oldPassword,
      newPassword: inputs.newPassword,
    };

    const response = await userService.updateAdminAsync(user.id, payload);
    setAggregateErrors({
      ...aggregateErrors,
      oldPassword: response.data.aggregateErrors?.oldPassword || "",
      newPassword: response.data.aggregateErrors?.newPassword || "",
    });
    if (response.ok) {
      setToast({
        show: true,
        messages: [`비밀번호를 변경했습니다.`],
      });
      closeModal();
    }
  };

  useEffect(() => {
    setInputs(initInputs);
  }, []);

  const disabled =
    !inputs.newPassword ||
    !inputs.oldPassword ||
    !inputs.newPasswordConfirm ||
    inputs.newPassword !== inputs.newPasswordConfirm;

  return (
    <Modal size="2xl" show={true} onClose={closeModal}>
      <Modal.Header className="p-[24px] h-[76px]">
        <H4>비밀번호 변경하기</H4>
      </Modal.Header>
      <Modal.Body>
        <div className="flex justify-center">
          <div className="flex flex-col gap-[20px]">
            <div className="flex flex-col">
              <TextInputLabel>현재 비밀번호</TextInputLabel>
              <div className="flex items-center">
                <TextInput
                  value={inputs.oldPassword}
                  placeholder="현재 비밀번호를 입력해주세요"
                  name="oldPassword"
                  onChange={handleTextInputChange}
                  type={showOldPassword ? "text" : "password"}
                  color={
                    !!aggregateErrors.oldPassword
                      ? TextInputColor.FAILURE
                      : TextInputColor.GRAY
                  }
                  rightIcon={() => {
                    return (
                      <PasswordEyeIcon
                        showPassword={showOldPassword}
                        isError={!!aggregateErrors.oldPassword}
                        onClick={toggleShowOldPassword}
                      />
                    );
                  }}
                  className="w-[572px] first:pointer-events-auto"
                />
              </div>
              {!!aggregateErrors.oldPassword && (
                <CustomHelperText
                  isError={!!aggregateErrors.oldPassword}
                  text={aggregateErrors.oldPassword}
                />
              )}
            </div>
            <div className="flex flex-col">
              <TextInputLabel>비밀번호</TextInputLabel>
              <div className="flex items-center">
                <TextInput
                  value={inputs.newPassword}
                  placeholder="새로운 비밀번호를 입력해주세요"
                  name="newPassword"
                  onChange={handleTextInputChange}
                  type={showNewPassword ? "text" : "password"}
                  color={
                    !!aggregateErrors.newPassword
                      ? TextInputColor.FAILURE
                      : TextInputColor.GRAY
                  }
                  rightIcon={() => {
                    return (
                      <PasswordEyeIcon
                        showPassword={showNewPassword}
                        isError={!!aggregateErrors.newPassword}
                        onClick={toggleShowNewPassword}
                      />
                    );
                  }}
                  className="w-[572px] first:first:pointer-events-auto"
                />
              </div>
              <CustomHelperText
                isError={!!aggregateErrors.newPassword}
                text={
                  aggregateErrors.newPassword ||
                  "특수문자, 영어, 숫자 조합으로 8~15자를 입력해주세요."
                }
              />
            </div>
            <div className="flex flex-col">
              <TextInputLabel>비밀번호 확인</TextInputLabel>
              <div className="flex items-center">
                <TextInput
                  value={inputs.newPasswordConfirm}
                  placeholder="비밀번호를 한 번 더 입력해주세요"
                  name="newPasswordConfirm"
                  onChange={handleTextInputChange}
                  type={showNewPasswordConfirm ? "text" : "password"}
                  color={
                    !!aggregateErrors.newPasswordConfirm
                      ? TextInputColor.FAILURE
                      : TextInputColor.GRAY
                  }
                  rightIcon={() => {
                    return (
                      <PasswordEyeIcon
                        showPassword={showNewPasswordConfirm}
                        isError={!!aggregateErrors.newPasswordConfirm}
                        onClick={toggleShowNewPasswordConfirm}
                      />
                    );
                  }}
                  className="w-[572px] first:pointer-events-auto"
                />
              </div>
              <CustomHelperText
                isError={!!aggregateErrors.newPasswordConfirm}
                text={
                  aggregateErrors.newPasswordConfirm ||
                  "특수문자, 영어, 숫자 조합으로 8~15자를 입력해주세요."
                }
              />
            </div>
            <Button
              disabled={disabled}
              color={disabled ? ButtonColor.DISABLED : ButtonColor.PRIMARY}
              onClick={handleSubmit}
              className="w-full"
              size={"xl"}
            >
              변경하기
            </Button>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};
